import { authenticatedRequest } from "./auth";
import { apiRequest } from "./http";

export type TeacherLinkInfo = {
  token: string;
  title: string | null;
  isActive: boolean;
  expiresAt: string | null;
  teacher: {
    uid: number;
    teacherName: string;
  };
};

export type TeacherStudentBinding = {
  bindingId: number;
  teacherUserId: number;
  studentUserId: number;
  linkId: number;
  studentName: string | null;
  alreadyBound: boolean;
  createdAt: string;
  updatedAt: string | null;
};

export type TeacherStudentBindingStatus = {
  binding: TeacherStudentBinding | null;
  requiresStudentName: boolean;
};

export type TeacherWebComparisonRunInput = {
  cropRect?: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
  durationSec: number;
  environment?: Record<string, unknown>;
  exportError?: string | null;
  fileSize?: number;
  idempotencyKey: string;
  fps?: number;
  mimeType?: string;
  modelVersion?: string;
  objectKey: string;
  originalFileName?: string;
  originalFileSize?: number;
  originalMimeType?: string;
  originalVideoDurationSec?: number;
  sourceKind: "trimmed" | "original_fallback";
  startTimeSec?: number;
  teacherToken: string;
  techType: string;
  videoHeight: number;
  videoWidth: number;
};

export type TeacherWebComparisonRun = {
  runId: string;
  videoId: string;
  status: string;
  teacherUserId: number;
  studentUserId: number;
  linkId: number;
  quotaUsageLogId: number | null;
  meterEventId: number | null;
};

export type TeacherWebGlbDownload = {
  objectKey: string;
};

export type TeacherWebGlbDownloadUrl = {
  slot: "first" | "second";
  objectKey: string;
  url: string;
  expiresAt: string;
  expiresIn: number;
  fileSize?: number;
};

export type TeacherWebGlbDownloads = {
  first?: TeacherWebGlbDownload;
  second?: TeacherWebGlbDownload;
};

export type TeacherWebQwenDescription = {
  strengths?: string[] | null;
  weaknesses?: string[] | null;
  improvements?: string[] | null;
  training_plan?: string[] | null;
};

export type TeacherWebLlmAnalysis = {
  summary?: string[] | null;
  start_frame?: TeacherWebQwenDescription | null;
  impact_frame?: TeacherWebQwenDescription | null;
  end_frame?: TeacherWebQwenDescription | null;
};

export type TeacherWebCompData = {
  body_center?: number[];
  directions?: Record<string, number[]>;
  frame_stats?: Record<string, unknown>;
  kinetic_chain_summary_left?: Record<string, unknown> | null;
  kinetic_chain_summary_right?: Record<string, unknown> | null;
  funny_stats?: Record<string, unknown> | null;
  key_frames?: number[];
  qwen_analysis?: TeacherWebLlmAnalysis | null;
};

export type TeacherWebKeyFrameImage = {
  frame_index?: number;
  object_key?: string;
};

export type TeacherWebKeyFrameImages = {
  frame_rate?: number;
  start?: TeacherWebKeyFrameImage;
  impact?: TeacherWebKeyFrameImage;
  end?: TeacherWebKeyFrameImage;
};

export type TeacherWebCompResults = {
  first_glb_object_key?: string;
  first_video_object_key?: string;
  second_glb_object_key?: string;
  key_frame_images?: {
    primary?: TeacherWebKeyFrameImages;
  };
  tech_type?: string | null;
  data?: TeacherWebCompData[];
};

export type TeacherWebResultAssetName =
  | "source_video"
  | "key_frame_start"
  | "key_frame_impact"
  | "key_frame_end";

export type TeacherWebResultAssetUrl = {
  asset: TeacherWebResultAssetName;
  objectKey: string;
  url: string;
  expiresAt: string;
  expiresIn: number;
};

export type TeacherWebComparisonRunStatus = {
  run_id: string;
  video_id: string;
  status: string;
  updated_at: string | null;
  teacher_user_id: number;
  student_user_id: number;
  student_name?: string | null;
  link_id: number;
  error_message?: string;
  comp_results?: TeacherWebCompResults;
  glb_downloads?: TeacherWebGlbDownloads;
};

export type TeacherWebLlmStatus = {
  run_id: string;
  status: "pending" | "completed" | "failed";
  llm_analysis: TeacherWebLlmAnalysis | null;
  updated_at: string | null;
};

export type TeacherDashboardQuota = {
  quotaCount: number;
  usedCount: number;
  remainingCount: number;
  downloadQuotaBytes: number;
  downloadUsedBytes: number;
  downloadRemainingBytes: number;
  activeContractCount: number;
  periodEnd: string | null;
};

export type TeacherDashboardPerformanceRun = {
  runId: string;
  studentUserId: number;
  linkId: number;
  techType?: string;
  createdAt: string;
  score: import("../features/result/standardness").StandardnessScore | null;
};
export type TeacherDashboardPerformance = {
  techType: string;
  days: number;
  startAt: string;
  endAt: string;
  runs: TeacherDashboardPerformanceRun[];
};

export async function getTeacherDashboardPerformance(techType: string, days: number, linkId: number | null, signal: AbortSignal): Promise<TeacherDashboardPerformance> {
  const params = new URLSearchParams({ techType, days: String(days) });
  if (linkId !== null) params.set("linkId", String(linkId));
  const response = await authenticatedRequest<{ data?: TeacherDashboardPerformance }>(`teacher-web/dashboard/performance?${params}`, { signal });
  if (!response.data) throw new Error("动作评分暂时无法读取");
  return response.data;
}

export type TeacherDashboardLink = {
  id: number;
  token: string;
  title: string | null;
  teacherName: string | null;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  studentCount: number;
  runCount: number;
  latestRunAt: string | null;
};

export type TeacherDashboardSummary = {
  teacherUserId: number;
  teacherName: string | null;
  linkCount: number;
  activeLinkCount: number;
  studentBindingCount: number;
  uniqueStudentCount: number;
  totalRuns: number;
  monthRuns: number;
  completedRuns: number;
  failedRuns: number;
  runningRuns: number;
  latestRunAt: string | null;
  quota: TeacherDashboardQuota;
  actionCounts: Record<string, number>;
  statusCounts: Record<string, number>;
  links: TeacherDashboardLink[];
  activityDays?: Array<{ date: string; count: number }>;
  activityHistory?: Array<{ date: string; techType: string; count: number }>;
  rosterStudentCount?: number | null;
  activeStudentCount?: number;
};

export type TeacherDashboardStudent = {
  actionCounts?: Record<string, number>;
  studentUserId: number;
  studentName: string | null;
  phoneMasked: string;
  linkId: number;
  linkTitle: string | null;
  boundAt: string;
  runCount: number;
  completedCount: number;
  failedCount: number;
  latestRunId: string | null;
  latestRunStatus: string | null;
  latestTechType: string | null;
  latestRunAt: string | null;
};

export type TeacherDashboardStudents = {
  total: number;
  students: TeacherDashboardStudent[];
};

export type TeacherDashboardRun = {
  runId: string;
  videoId: string;
  linkId: number;
  linkTitle: string | null;
  techType: string;
  status: string;
  hasReport: boolean;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
};

export type TeacherDashboardStudentDetail = {
  studentUserId: number;
  studentName: string | null;
  phoneMasked: string;
  bindings: Array<{
    linkId: number;
    linkTitle: string | null;
    boundAt: string;
  }>;
  actionCounts: Record<string, number>;
  runs: TeacherDashboardRun[];
  totalRuns?: number;
  offset?: number;
  limit?: number;
  hasMore?: boolean;
};

export async function getTeacherLink(token: string, signal?: AbortSignal): Promise<TeacherLinkInfo> {
  return apiRequest<TeacherLinkInfo>(`teacher-web/links/${encodeURIComponent(token)}`, { signal });
}

export async function getTeacherLinkBinding(
  token: string,
  signal?: AbortSignal,
): Promise<TeacherStudentBindingStatus> {
  return authenticatedRequest<TeacherStudentBindingStatus>(
    `teacher-web/links/${encodeURIComponent(token)}/binding`,
    { signal },
  );
}

export async function bindTeacherLink(token: string, studentName: string): Promise<TeacherStudentBinding> {
  return authenticatedRequest<TeacherStudentBinding>(`teacher-web/links/${encodeURIComponent(token)}/bind`, {
    method: "POST",
    body: { studentName },
  });
}

export async function startTeacherWebComparisonRun(
  input: TeacherWebComparisonRunInput,
): Promise<TeacherWebComparisonRun> {
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherWebComparisonRun }>("teacher-web/comparison/model-runs", {
    method: "POST",
    body: input,
  });
  if (!response.data) {
    throw new Error("AI教练任务创建失败");
  }
  return response.data;
}

export async function getTeacherWebComparisonRunStatus(
  runId: string,
  signal?: AbortSignal,
): Promise<TeacherWebComparisonRunStatus> {
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherWebComparisonRunStatus }>(
    `teacher-web/comparison/model-runs/${encodeURIComponent(runId)}`,
    { signal },
  );
  if (!response.data) {
    throw new Error("结果状态读取失败");
  }
  return response.data;
}

export async function getTeacherWebGlbDownloadUrl(
  runId: string,
  slot: "first" | "second",
  signal?: AbortSignal,
): Promise<TeacherWebGlbDownloadUrl> {
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherWebGlbDownloadUrl }>(
    `teacher-web/comparison/model-runs/${encodeURIComponent(runId)}/glb-url`,
    {
      method: "POST",
      body: { slot },
      signal,
    },
  );
  if (!response.data) {
    throw new Error("模型下载链接获取失败");
  }
  return response.data;
}

export async function getTeacherWebResultAssetUrls(
  runId: string,
  assets: TeacherWebResultAssetName[],
  signal?: AbortSignal,
): Promise<TeacherWebResultAssetUrl[]> {
  const response = await authenticatedRequest<{
    success: boolean;
    data?: { items: TeacherWebResultAssetUrl[] };
  }>(`teacher-web/comparison/model-runs/${encodeURIComponent(runId)}/asset-urls`, {
    method: "POST",
    body: { assets },
    signal,
  });
  if (!response.data) {
    throw new Error("结果资源下载链接获取失败");
  }
  return response.data.items;
}

export async function getTeacherWebLlmStatus(
  runId: string,
  signal?: AbortSignal,
): Promise<TeacherWebLlmStatus> {
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherWebLlmStatus }>(
    `teacher-web/comparison/model-runs/${encodeURIComponent(runId)}/llm-status`,
    { signal },
  );
  if (!response.data) {
    throw new Error("AI 报告状态读取失败");
  }
  return response.data;
}

export async function getTeacherDashboardSummary(
  linkId?: number | null,
  signal?: AbortSignal,
): Promise<TeacherDashboardSummary> {
  const query = linkId ? `?linkId=${encodeURIComponent(String(linkId))}` : "";
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherDashboardSummary }>(
    `teacher-web/dashboard/summary${query}`,
    { signal },
  );
  if (!response.data) {
    throw new Error("老师看板读取失败");
  }
  return response.data;
}

export async function getTeacherDashboardStudents(
  linkId?: number | null,
  signal?: AbortSignal,
): Promise<TeacherDashboardStudents> {
  const query = linkId ? `?linkId=${encodeURIComponent(String(linkId))}` : "";
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherDashboardStudents }>(
    `teacher-web/dashboard/students${query}`,
    { signal },
  );
  if (!response.data) {
    throw new Error("学生列表读取失败");
  }
  return response.data;
}

export async function getTeacherDashboardStudentDetail(
  studentUserId: number,
  linkId?: number | null,
  signal?: AbortSignal,
  page = 0,
  pageSize = 10,
  techType = "all",
): Promise<TeacherDashboardStudentDetail> {
  const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize), techType });
  if (linkId != null) params.set("linkId", String(linkId));
  const query = `?${params}`;
  const response = await authenticatedRequest<{ success: boolean; data?: TeacherDashboardStudentDetail }>(
    `teacher-web/dashboard/students/${encodeURIComponent(String(studentUserId))}${query}`,
    { signal },
  );
  if (!response.data) {
    throw new Error("学生详情读取失败");
  }
  return response.data;
}
